import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Plus } from "lucide-react";
import { api } from "@convex/_generated/api";
import type { Doc } from "@convex/_generated/dataModel";
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser";
import { authClient } from "@/features/auth/lib/authClient";
import { LeagueSettingsCard } from "@/features/leagues/components/LeagueSettingsCard";
import { useT } from "@/shared/i18n";
import { LanguageSwitcher } from "@/shared/layout/LanguageSwitcher";
import { PageContainer } from "@/shared/layout/PageContainer";
import { PageHeader } from "@/shared/layout/PageHeader";
import { Button } from "@/shared/ui/Button";
import { Skeleton } from "@/shared/ui/Skeleton";

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});

type TabId = "account" | "language" | "leagues";

const TABS: ReadonlyArray<{ id: TabId; key: string }> = [
  { id: "account", key: "settings.tabs.account" },
  { id: "leagues", key: "settings.tabs.leagues" },
  { id: "language", key: "settings.tabs.language" },
];

function SettingsPage() {
  const t = useT();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const myLeagues = useQuery(api.leagues.listMyLeagues, {});

  // Persist selection across reloads/back-forward — the page is one place but
  // three concerns, and users tend to deep-link to the same one repeatedly.
  const [tab, setTab] = useState<TabId>(() => {
    if (typeof window === "undefined") return "account";
    const stored = window.localStorage.getItem("settings.tab");
    return stored === "leagues" || stored === "language" ? stored : "account";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("settings.tab", tab);
    }
  }, [tab]);

  async function signOut() {
    await authClient.signOut();
    void navigate({ to: "/auth/sign-in" });
  }

  return (
    <PageContainer width="default">
      <PageHeader
        eyebrow={t("nav.settings")}
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6 lg:gap-10 mt-8 lg:mt-10">
        {/* Side nav — collapses to a horizontal pill bar on small screens. */}
        <nav
          aria-label={t("nav.settings")}
          className="lg:sticky lg:top-6 lg:self-start"
        >
          <ul className="flex lg:flex-col gap-1 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0">
            {TABS.map((tabDef) => {
              const active = tab === tabDef.id;
              return (
                <li key={tabDef.id} className="shrink-0 lg:shrink">
                  <button
                    type="button"
                    onClick={() => setTab(tabDef.id)}
                    aria-current={active ? "page" : undefined}
                    className={
                      "w-full text-left px-3 py-2 rounded-sm text-sm font-medium tracking-tight transition-colors whitespace-nowrap " +
                      (active
                        ? "bg-ink text-bg"
                        : "text-muted hover:text-ink hover:bg-surface-2")
                    }
                  >
                    {t(tabDef.key)}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-w-0">
          {tab === "account" ? <AccountPanel onSignOut={() => void signOut()} /> : null}
          {tab === "language" ? <LanguagePanel /> : null}
          {tab === "leagues" ? (
            <LeaguesPanel
              myLeagues={myLeagues}
              myUserId={user?._id ?? null}
            />
          ) : null}
        </div>
      </div>
    </PageContainer>
  );
}

function AccountPanel({ onSignOut }: { onSignOut: () => void }) {
  const t = useT();
  const user = useCurrentUser();
  return (
    <div className="flex flex-col gap-6">
      <Panel title={t("settings.signedInAs")}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-accent text-bg font-mono font-bold text-sm">
              {user?.name ? initials(user.name) : "—"}
            </span>
            <div className="min-w-0">
              <p className="font-medium text-ink truncate">{user?.name ?? "—"}</p>
              <p className="text-sm text-muted truncate">{user?.email ?? "—"}</p>
            </div>
          </div>
          <Button variant="secondary" onClick={onSignOut}>
            {t("settings.signOut")}
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function LanguagePanel() {
  const t = useT();
  return (
    <Panel title={t("settings.language")} description={t("settings.languageHelp")}>
      <LanguageSwitcher />
    </Panel>
  );
}

function LeaguesPanel({
  myLeagues,
  myUserId,
}: {
  myLeagues:
    | Array<{
        league: Doc<"leagues">;
        membership: Doc<"leagueMembers">;
      }>
    | undefined;
  myUserId: string | null;
}) {
  const t = useT();

  if (myLeagues === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (myLeagues.length === 0) {
    return (
      <Panel title={t("settings.leagues.empty.title")} description={t("settings.leagues.empty.body")}>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-ink text-bg text-sm font-medium hover:bg-accent transition-colors"
          >
            <Plus size={14} />
            {t("home.createLeague")}
          </Link>
        </div>
      </Panel>
    );
  }
  return (
    <div className="flex flex-col gap-6">
      {myLeagues.map(({ league, membership }) => (
        <LeagueWithMembers
          key={league._id}
          leagueId={league._id}
          fallbackLeague={league}
          fallbackMembership={membership}
          myUserId={myUserId}
        />
      ))}
    </div>
  );
}

/**
 * Live-subscribed league + members for the settings card. We pass the
 * `listMyLeagues` payload as a fallback so the card renders immediately, then
 * the more detailed `getLeague` query takes over for fully reactive members
 * and commissioner status.
 */
function LeagueWithMembers({
  leagueId,
  fallbackLeague,
  fallbackMembership: _fallbackMembership,
  myUserId,
}: {
  leagueId: Doc<"leagues">["_id"];
  fallbackLeague: Doc<"leagues">;
  fallbackMembership: Doc<"leagueMembers">;
  myUserId: string | null;
}) {
  const data = useQuery(api.leagues.getLeague, { leagueId });

  if (data === undefined) {
    return <Skeleton className="h-72 w-full" />;
  }
  // The query revoked access (e.g. commissioner removed us mid-session).
  if (data === null) {
    return null;
  }

  return (
    <LeagueSettingsCard
      league={data.league ?? fallbackLeague}
      members={data.members}
      isCommissioner={data.isCommissioner}
      myUserId={myUserId}
    />
  );
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface p-5 sm:p-6 shadow-sm">
      <header className="mb-4">
        <h2 className="font-display text-lg sm:text-xl text-ink tracking-tight">{title}</h2>
        {description ? (
          <p className="text-sm text-muted mt-1">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

